import styled, { keyframes } from "styled-components";
import { BroadcastAppCard as BroadcastAppCardStyles } from "../../BroadcastView.styles";

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const popIn = keyframes`
  0%   { transform: scale(0.6); opacity: 0; }
  70%  { transform: scale(1.1); }
  100% { transform: scale(1);   opacity: 1; }
`;

export const FieldWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const FieldAdornment = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  pointer-events: none;
`;

export const FieldSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #e2e8f0;
  border-top-color: #2c5f4e;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

export const FieldCheckIcon = styled.div`
  width: 18px;
  height: 18px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${popIn} 0.3s ease forwards;

  &::after {
    content: "";
    width: 5px;
    height: 9px;
    border-right: 2px solid #fff;
    border-bottom: 2px solid #fff;
    transform: rotate(40deg) translate(-1px, -1px);
  }
`;

export const FieldErrorIcon = styled.div`
  width: 18px;
  height: 18px;
  background: linear-gradient(135deg, #ef4444, #b91c1c);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${popIn} 0.3s ease forwards;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  line-height: 1;

  &::after {
    content: "✕";
  }
`;

export const NameStatusText = styled.span`
  font-size: 0.78rem;
  font-family: "Inter", sans-serif;
  color: ${({ $status }) =>
    $status === "available"
      ? "#16a34a"
      : $status === "taken"
        ? "#b91c1c"
        : "#94a3b8"};
  display: flex;
  align-items: center;
  gap: 0.3rem;
  animation: ${fadeIn} 0.2s ease;
  min-height: 1rem;
`;

export const SelectTemplateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

export const SelectTemplateHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SelectTemplateBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
`;

export const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 40px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  font-size: 15px;
  color: #1e293b;
  transition: all 0.2s ease;
  outline: none;

  &:focus {
    background-color: #ffffff;
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

export const SearchIcon = styled.svg`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: #94a3b8;
  pointer-events: none;
`;

export const RefetchSpinner = styled.div`
  border: 2px solid #e2e8f0;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: ${spin} 1s linear infinite;
  margin-left: 12px;
`;

// --- Grid & Cards ---
export const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
  width: 100%;
`;

export const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 2px 4px -1px rgba(0, 0, 0, 0.03);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  animation: ${fadeIn} 0.4s ease forwards;
  position: relative;
  overflow: hidden;

  ${({ $selected }) =>
    $selected &&
    `
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px #3b82f6, 0 10px 15px -3px rgba(59, 130, 246, 0.2);
  `}

  &:hover {
    transform: translateY(-4px);
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  word-break: break-word;
  line-height: 1.4;
`;

export const Badge = styled.span`
  background-color: ${({ $status }) =>
    $status === "accepted"
      ? "#dcfce7"
      : $status === "rejected"
        ? "#fee2e2"
        : "#f1f5f9"};
  color: ${({ $status }) =>
    $status === "accepted"
      ? "#166534"
      : $status === "rejected"
        ? "#991b1b"
        : "#475569"};
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  letter-spacing: 0.3px;
`;

export const CardCategory = styled.div`
  font-size: 13px;
  color: #64748b;
  margin-bottom: 12px;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

export const CardBodyPreview = styled.div`
  font-size: 14px;
  color: #475569;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #f8fafc;
  padding: 12px;
  border-radius: 8px;
`;

// --- Skeletons ---
export const SkeletonCard = styled(Card)`
  cursor: default;
  &:hover {
    transform: none;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  }
`;

export const SkeletonBox = styled.div`
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite linear;
  border-radius: ${({ $radius }) => $radius || "4px"};
  width: ${({ $width }) => $width || "100%"};
  height: ${({ $height }) => $height || "20px"};
  margin-bottom: ${({ $mb }) => $mb || "0"};
`;

// --- Pagination ---
export const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
  flex-wrap: wrap;
`;

export const PageButton = styled.button`
  background-color: ${({ $active }) => ($active ? "#3b82f6" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#475569")};
  border: 1px solid ${({ $active }) => ($active ? "#3b82f6" : "#e2e8f0")};
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover {
    background-color: ${({ $active, disabled }) =>
      disabled ? "" : $active ? "#2563eb" : "#f1f5f9"};
    border-color: ${({ $active, disabled }) =>
      disabled ? "#e2e8f0" : $active ? "#2563eb" : "#cbd5e1"};
  }
`;

// --- Empty State ---
export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
  background: #f8fafc;
  border-radius: 16px;
  border: 1px dashed #cbd5e1;
  width: 100%;
`;

export const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #e0e7ff;
  color: #4f46e5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  svg {
    width: 32px;
    height: 32px;
  }
`;

export const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

export const EmptyStateText = styled.p`
  font-size: 15px;
  color: #64748b;
  margin: 0;
  max-width: 400px;
  line-height: 1.5;
`;

// ----------------------

export const BroadcastAppCard = styled(BroadcastAppCardStyles)`
  gap: 2rem;
`;

export const FormContainer = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const FormInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 0.95rem;
  font-family: "Inter", sans-serif;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
  transition: all 0.2s ease;

  &:focus {
    border-color: #2c5f4e;
    box-shadow: 0 0 0 3px rgba(44, 95, 78, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

export const FormInputDescription = styled.span`
  font-size: 0.8rem;
  color: #9ca3af;
  font-family: "Inter", sans-serif;
`;

export const FormInputLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.25rem;
  font-family: "Plus Jakarta Sans", sans-serif;
`;

export const FormInputError = styled.span`
  font-size: 0.8rem;
  color: #991b1b;
  font-family: "Inter", sans-serif;
`;

export const Container = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 1.5rem;
`;

export const ListContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: #f9fafb;
  padding: 1.5rem;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
`;

export const ListTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: "Plus Jakarta Sans", sans-serif;

  &::before {
    content: "";
    width: 12px;
    height: 12px;
    background-color: #2c5f4e;
    border-radius: 50%;
  }
`;

export const ListContent = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-family: "Inter", sans-serif;
`;

export const ListContentItem = styled.li`
  font-size: 0.9rem;
  color: #4b5563;
  padding-left: 1.5rem;
  position: relative;
  line-height: 1.6;
`;

export const ContainerActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
`;

export const WhatsAppTemplate = styled.div`
  padding: 1rem;
  border-radius: 1rem;
  background-color: #eef2f6;
  min-height: 420px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 500px;
`;

export const RichTextContainer = styled.div`
  background-color: #eaf8ef;
  padding: 1rem;
  display: block;
  word-wrap: break-word;
  word-break: break-word;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #1f2937;

  p {
    margin: 0;
    padding: 0;
    min-height: 1.4em;
  }

  b, strong {
    font-weight: 700;
  }

  i, em {
    font-style: italic;
  }

  s, del, strike {
    text-decoration: line-through;
  }

  code {
    font-family: "SFMono-Regular", "Consolas", "Liberation Mono", "Menlo", monospace;
    font-size: 0.82em;
    background-color: rgba(0, 0, 0, 0.06);
    padding: 1px 4px;
    border-radius: 3px;
  }

  .highlighted-variable,
  .variable-chip {
    display: inline;
    padding: 2px 6px;
    margin: 0 1px;
    background-color: #128c7e;
    color: white;
    border-radius: 4px;
    font-size: 0.813rem;
    font-weight: 500;
    border: none;
    line-height: 1;
    white-space: nowrap;
  }
`;

export const WhatsAppMessage = styled.p`
  background-color: #fff;
  padding: 1rem;
  border-radius: 1rem;
  display: flex;
  width: max-content;
  max-width: 250px;
`;

export const WhatsAppFooter = styled.p`
  border-radius: 0 0 1rem 1rem;
  background-color: #f8fafc;
  border: 1px solid #eceff1ff;
  padding: 1rem;
  font-size: 0.75rem;
  color: #212121;
`;

export const FileInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
`;

export const DropZoneContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  background-color: #f9fafb;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 200px;

  &:hover {
    border-color: #2c5f4e;
    background-color: #f2f9f7;
  }

  ${(props) =>
    props.disabled &&
    `
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  `}

  ${(props) =>
    props.hasFile &&
    `
    border-color: #2C5F4E;
    background-color: #F2F9F7;
  `}
`;

export const ExcelIcon = styled.div`
  width: 64px;
  height: 64px;
  background-color: #1d6f42;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  position: relative;

  &::after {
    content: "X";
    color: white;
    font-size: 32px;
    font-weight: bold;
    font-family: "Arial", sans-serif;
  }
`;

export const DropZoneText = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  font-family: "Plus Jakarta Sans", sans-serif;
`;

export const DropZoneDescription = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  text-align: center;
  font-family: "Inter", sans-serif;
`;

export const SummaryPanel = styled.div`
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  min-width: 320px;
  max-width: 320px;
`;

export const SummaryTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #212121;
  margin-bottom: 1.5rem;

  &::before {
    content: "";
    width: 16px;
    height: 16px;
    background-color: #128c7e;
    border-radius: 2px;
  }
`;

export const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: ${(props) => (props.noBorder ? "none" : "1px solid #e5e7eb")};
`;

export const SummaryLabel = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
`;

export const SummaryValue = styled.span`
  font-size: ${(props) => (props.large ? "1.5rem" : "0.875rem")};
  font-weight: ${(props) => (props.bold ? "700" : "500")};
  color: #212121;
`;

export const SummaryNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f0f9ff;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;

  &::before {
    content: "\u2139";
    font-size: 1rem;
    color: #3b82f6;
    flex-shrink: 0;
  }
`;

export const InstructionsPanel = styled.div`
  background-color: #fff9e6;
  border: 1px solid #ffd966;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

export const InstructionsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #212121;
  margin-bottom: 1rem;

  &::before {
    content: "📋";
    font-size: 1.25rem;
  }
`;

export const InstructionsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const InstructionItem = styled.li`
  font-size: 0.75rem;
  color: #424242;
  padding-left: 1.5rem;
  position: relative;
  line-height: 1.5;

  &::before {
    content: "•";
    position: absolute;
    left: 0.5rem;
    color: #ffa726;
    font-weight: bold;
  }
`;

export const ExampleBox = styled.div`
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 0.75rem;
  margin-top: 0.75rem;
  font-family: "Courier New", monospace;
  font-size: 0.7rem;
  color: #212121;
  overflow-x: auto;
`;

export const ExampleTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: #616161;
  margin-bottom: 0.5rem;
  font-family:
    system-ui,
    -apple-system,
    sans-serif;
`;

export const ScheduleContainer = styled.div`
  display: flex;
  gap: 2rem;
  align-items: flex-start;
`;

export const SelectContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const SelectLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  font-family: "Plus Jakarta Sans", sans-serif;
`;

export const Select = styled.select`
  width: 100%;
  padding: 12px;
  font-size: 0.95rem;
  color: #1f2937;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: white;
  cursor: pointer;
  outline: none;
  appearance: none;
  font-family: "Inter", sans-serif;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  transition: all 0.2s ease;

  &:focus {
    border-color: #2c5f4e;
    box-shadow: 0 0 0 3px rgba(44, 95, 78, 0.1);
  }
`;

export const TipPanel = styled.div`
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  min-width: 400px;
  max-width: 400px;
`;

export const TipTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #212121;
  margin-bottom: 1rem;
`;

export const ClockIcon = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #212121;
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: "";
    position: absolute;
    width: 1px;
    height: 6px;
    background-color: #212121;
    top: 3px;
  }

  &::after {
    content: "";
    position: absolute;
    width: 4px;
    height: 1px;
    background-color: #212121;
    right: 7px;
    top: 8px;
  }
`;

export const TipText = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.6;
`;

export const DateTimeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

export const DateInput = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 0.95rem;
  color: #1f2937;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: white;
  outline: none;
  font-family: "Inter", sans-serif;
  transition: all 0.2s ease;

  &:focus {
    border-color: #2c5f4e;
    box-shadow: 0 0 0 3px rgba(44, 95, 78, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

export const TimeInput = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 0.95rem;
  color: #1f2937;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: white;
  outline: none;
  font-family: "Inter", sans-serif;
  transition: all 0.2s ease;

  &:focus {
    border-color: #2c5f4e;
    box-shadow: 0 0 0 3px rgba(44, 95, 78, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

export const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

export const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #2c5f4e;
  cursor: pointer;
`;

export const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  color: #374151;
  cursor: pointer;
  font-family: "Inter", sans-serif;
`;

export const ResumeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 1.5rem;
`;

export const DetailsPanel = styled.div`
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const DetailsPanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #212121;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

export const DetailsIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: "☰";
    font-size: 1.25rem;
    color: #212121;
  }
`;

export const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const DetailLabel = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
`;

export const DetailValue = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #212121;
`;

export const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  gap: 2rem;
`;

export const SuccessIcon = styled.div`
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: 0 10px 30px rgba(37, 211, 102, 0.3);
  animation: scaleIn 0.5s ease-out;

  @keyframes scaleIn {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  &::after {
    content: "✓";
    color: white;
    font-size: 64px;
    font-weight: bold;
  }
`;

export const SuccessTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #212121;
  margin: 0;
`;

export const SuccessSubtitle = styled.p`
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  max-width: 500px;
`;

export const SuccessDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  width: 100%;
  max-width: 600px;
  margin-top: 1rem;
`;

export const SuccessDetailCard = styled.div`
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: left;
`;

export const SuccessDetailLabel = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
`;

export const SuccessDetailValue = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: #212121;
  word-break: break-word;
`;

export const SuccessActionsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: center;
  flex-wrap: wrap;
`;

export const InfoBanner = styled.div`
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  max-width: 600px;
  width: 100%;

  &::before {
    content: "ℹ";
    font-size: 1.5rem;
    color: #0284c7;
    flex-shrink: 0;
  }
`;

export const PreviewContainer = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: #fff;
`;

export const PreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const PreviewTitle = styled.h4`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #212121;
`;

export const CountryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 2px 6px;
  background-color: #f3f4f6;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  border: 1px solid #e5e7eb;
`;

export const DuplicateBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  background-color: #fff9c4;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #f57f17;
  border: 1px solid #fbc02d;
`;

export const SafeInput = styled.input`
  width: 100%;
  padding: 4px 8px;
  font-size: 0.875rem;
  border: 1px solid transparent;
  border-radius: 4px;
  background-color: transparent;
  transition: all 0.2s;

  &:hover {
    border-color: #d1d5db;
    background-color: #fff;
  }

  &:focus {
    outline: none;
    border-color: #2c5f4e;
    background-color: #fff;
    box-shadow: 0 0 0 2px rgba(44, 95, 78, 0.1);
  }
`;

export const SidebarSection = styled.div`
  padding: 0.75rem 0;
  border-bottom: 1px solid #e5e7eb;
`;

export const CountryStatItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  font-size: 0.875rem;
`;

export const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #4b5563;
`;

export const StatValue = styled.span`
  font-weight: 600;
  color: #111827;
`;

export const CountryPricingItem = styled.div`
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
`;

export const CountryPricingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
`;

export const CountryPricingDetail = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  color: #9ca3af;
  margin-top: 2px;
  padding-left: 1.75rem;
`;

export const ActionButton = styled.button`
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const EditButton = styled(ActionButton)`
  padding: 4px;
  border: none;
  background: transparent;
  color: #6b7280;

  &:hover {
    color: #2c5f4e;
    background-color: #f0fdf4;
  }
`;

export const DeleteButton = styled(ActionButton)`
  padding: 4px;
  border: none;
  background: transparent;
  color: #6b7280;

  &:hover {
    color: #ef4444;
    background-color: #fef2f2;
  }
`;

export const SaveButton = styled(ActionButton)`
  background-color: #2c5f4e;
  color: white;
  border: none;
  padding: 4px 8px;
  font-size: 0.75rem;

  &:hover {
    background-color: #1e4236;
  }
`;

export const CancelButton = styled(ActionButton)`
  border: none;
  background: transparent;
  color: #6b7280;
  padding: 4px 8px;
  font-size: 0.75rem;

  &:hover {
    color: #374151;
    background-color: #f3f4f6;
  }
`;

export const PreviewContent = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

export const PreviewMedia = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 4px;
  overflow: hidden;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid #eee;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const PreviewInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow: hidden;
`;

export const FileName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #212121;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FileSize = styled.span`
  font-size: 0.75rem;
  color: #757575;
`;

export const FileTypeIcon = styled.div`
  font-size: 2rem;
  color: #757575;
`;

export const ReplaceButton = styled.button`
  background: none;
  border: none;
  color: #d32f2f;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;

  &:hover {
    color: #b71c1c;
  }
`;

export const InfoBannerText = styled.p`
  font-size: 0.875rem;
  color: #0c4a6e;
  margin: 0;
  line-height: 1.5;
`;

export const WarningBanner = styled.div`
  background-color: #fff9c4;
  border: 1px solid #fbc02d;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  max-width: 600px;
  width: 100%;
  margin-top: 1rem;

  &::before {
    content: "⚠";
    font-size: 1.25rem;
    color: #f57f17;
    flex-shrink: 0;
  }
`;

export const WarningText = styled.span`
  color: #f57f17;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
`;

export const ButtonListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

export const ButtonInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
`;

export const RemoveButtonIcon = styled.button`
  background: none;
  border: 1px solid #ef4444;
  color: #ef4444;
  border-radius: 999px;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #b91c1c;
    background-color: #fee2e2;
  }

  i {
    font-size: 1rem;
  }
`;

export const AddButtonAction = styled.button`
  background: none;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.75rem;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    border-color: #94a3b8;
    color: #475569;
    background-color: #f8fafc;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PreviewFooter = styled.div`
  border-top: 1px solid #e5e5e5;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
`;

export const WhatsAppButton = styled.div`
  background-color: #fff;
  color: #00a5f4;
  text-align: center;
  padding: 10px;
  font-size: 14px;
  font-weight: 500;
  border-top: 1px solid #f0f0f0;
  cursor: pointer;
  width: 100%;

  &:hover {
    background-color: #f9f9f9;
  }
`;

export const ReviewContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  height: 600px;
  width: 100%;
`;

export const Sidebar = styled.div`
  width: 280px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex-shrink: 0;
`;

export const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow: hidden;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
`;

export const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  background-color: #fafafa;
`;

export const Tab = styled.button`
  padding: 1rem 1.5rem;
  background: ${(props) => (props.active ? "#fff" : "transparent")};
  border: none;
  border-bottom: 2px solid
    ${(props) => (props.active ? "#128C7E" : "transparent")};
  color: ${(props) => (props.active ? "#128C7E" : "#757575")};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;

  &:hover {
    background-color: ${(props) => (props.active ? "#fff" : "#f5f5f5")};
  }
`;

export const TableContainer = styled.div`
  flex: 1;
  overflow: hidden;
`;

export const TableRowStyles = `
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
  box-sizing: border-box;
  padding: 0 1rem;
`;

export const TableRow = styled.div`
  ${TableRowStyles}
  &:hover {
    background-color: #f9f9f9;
  }
`;

export const TableHeader = styled.div`
  ${TableRowStyles}
  background-color: #f5f5f5;
  font-weight: 600;
  border-bottom: 1px solid #e0e0e0;
  color: #424242;
  height: 50px;
  flex-shrink: 0;
`;

export const TableCell = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.875rem;
  padding-right: 1rem;
  flex: ${(props) => props.flex || 1};
  width: ${(props) => (props.width ? props.width : "auto")};

  ${(props) =>
    props.error &&
    `
    color: #d32f2f;
    font-weight: 500;
  `}
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9e9e9e;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
`;

export const ActionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

export const ReuploadLink = styled.button`
  background: none;
  border: none;
  color: #128c7e;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0;

  &:hover {
    color: #0e6b5e;
  }
`;

export const DownloadExampleLink = styled.button`
  background: none;
  border: none;
  color: #1d6f42;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

export const ActionItemContainer = styled.div`
  border: 1px solid #e5e7eb;
  padding: 1.5rem;
  border-radius: 12px;
  background-color: white;
  margin-bottom: 1.5rem;
  position: relative;
`;

export const ActionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f3f4f6;
`;

export const ActionTitle = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  font-family: "Plus Jakarta Sans", sans-serif;
`;

export const ActionLabelDisplay = styled.span`
  background-color: #e5e7eb;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
`;

export const InfoAlert = styled.div`
  background-color: #fffaf0;
  border: 1px solid #feebc8;
  color: #744210;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &::before {
    content: "ⓘ";
    font-size: 1.2rem;
  }
`;

export const BotIntentsContainer = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

export const BotIntentRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  align-items: flex-start;
`;

// Country Selector Styles
export const CountrySelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1.5rem;
  max-width: 500px;
  margin: 0 auto;
`;

export const CountrySelectTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  text-align: center;
  font-family: "Plus Jakarta Sans", sans-serif;
`;

export const CountrySelectDescription = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  text-align: center;
  margin: 0;
  line-height: 1.5;
  font-family: "Inter", sans-serif;
`;

export const ComboboxContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 360px;
`;

export const ComboboxInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const ComboboxInput = styled.input`
  width: 100%;
  padding: 12px 12px 12px 40px;
  font-size: 0.95rem;
  font-family: "Inter", sans-serif;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
  transition: all 0.2s ease;
  background-color: white;

  &:focus {
    border-color: #2c5f4e;
    box-shadow: 0 0 0 3px rgba(44, 95, 78, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

export const ComboboxSearchIcon = styled.span`
  position: absolute;
  left: 12px;
  display: flex;
  align-items: center;
  pointer-events: none;
  color: #9ca3af;
  font-size: 1rem;
`;

export const ComboboxDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 280px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 50;
`;

export const ComboboxOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  font-family: "Inter", sans-serif;
  font-size: 0.875rem;

  &:hover {
    background-color: #f3f4f6;
  }

  ${(props) =>
    props.selected &&
    `
    background-color: #f0fdf4;
    font-weight: 500;
  `}
`;

export const ComboboxOptionFlag = styled.span`
  font-size: 1.25rem;
  line-height: 1;
`;

export const ComboboxOptionText = styled.span`
  flex: 1;
  color: #374151;
`;

export const ComboboxOptionCode = styled.span`
  color: #9ca3af;
  font-size: 0.8rem;
`;

export const ComboboxNoResults = styled.div`
  padding: 12px;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
  font-family: "Inter", sans-serif;
`;

export const SelectedCountryDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 12px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: "Inter", sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
`;

export const SelectedCountryFlag = styled.span`
  font-size: 1.5rem;
`;

export const SelectedCountryName = styled.span`
  flex: 1;
  color: #1f2937;
  font-weight: 500;
`;

export const SelectedCountryCode = styled.span`
  color: #6b7280;
  font-size: 0.875rem;
`;

export const CountrySelectActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

// Phone Notification Styles
export const PhoneInputWrapper = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`;

export const PhoneInputField = styled.input`
  flex: 1;
  padding: 12px;
  font-size: 0.95rem;
  font-family: "Inter", sans-serif;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
  transition: all 0.2s ease;

  &:focus {
    border-color: #2c5f4e;
    box-shadow: 0 0 0 3px rgba(44, 95, 78, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }

  ${(props) =>
    props.error &&
    `
    border-color: #ef4444;
    &:focus {
      border-color: #ef4444;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
    }
  `}
`;

export const AddPhoneButton = styled.button`
  padding: 12px 20px;
  background-color: #2c5f4e;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Plus Jakarta Sans", sans-serif;
  white-space: nowrap;

  &:hover {
    background-color: #1e4236;
  }

  &:disabled {
    background-color: #d1d5db;
    cursor: not-allowed;
  }

  &:active {
    transform: scale(0.98);
  }
`;

export const PhoneChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  min-height: 60px;
  padding: 1rem;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

export const PhoneChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 12px;
  background-color: #2c5f4e;
  color: white;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: "Inter", sans-serif;
  transition: all 0.2s ease;

  &:hover {
    background-color: #1e4236;
  }
`;

export const PhoneChipText = styled.span`
  user-select: none;
`;

export const PhoneChipRemove = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  transition: all 0.2s ease;
  font-size: 1rem;
  line-height: 1;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  &:active {
    transform: scale(0.9);
  }
`;

export const EmptyPhoneState = styled.div`
  width: 100%;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
  font-family: "Inter", sans-serif;
  padding: 1rem 0;
`;

export const NotificationPanel = styled.div`
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 1.5rem;
  min-width: 400px;
  max-width: 400px;
`;

export const NotificationTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #212121;
  margin-bottom: 1rem;
  font-family: "Plus Jakarta Sans", sans-serif;
`;

export const NotificationIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: "🔔";
    font-size: 1.25rem;
  }
`;

export const NotificationText = styled.p`
  font-size: 0.875rem;
  color: #0c4a6e;
  line-height: 1.6;
  margin: 0;
  font-family: "Inter", sans-serif;
`;

export const NotificationContainer = styled.div`
  display: flex;
  gap: 2rem;
  align-items: flex-start;
`;

// --- Country Phone Selector Styles ---
export const CountryPhoneRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0;
  width: 100%;
`;

export const CountrySelectorTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 12px 10px;
  background-color: #f9fafb;
  border: 1px solid #d1d5db;
  border-right: none;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  transition: all 0.2s ease;
  outline: none;
  flex-shrink: 0;

  &:hover {
    background-color: #f3f4f6;
    border-color: #9ca3af;
    color: #111827;
  }

  &:focus {
    border-color: #2c5f4e;
    box-shadow: -3px 0 0 3px rgba(44, 95, 78, 0.1), 0 -3px 0 3px rgba(44, 95, 78, 0.1), 0 3px 0 3px rgba(44, 95, 78, 0.1);
    z-index: 1;
    position: relative;
  }

  &:active {
    background-color: #e5e7eb;
  }
`;

export const CountrySelectorDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 280px;
  max-height: 300px;
  overflow-y: auto;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 10px 15px -3px rgba(0, 0, 0, 0.08);
  z-index: 50;
`;

export const CountrySelectorOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 9px 12px;
  cursor: pointer;
  font-size: 0.875rem;
  font-family: "Inter", sans-serif;
  color: #374151;
  transition: background-color 0.15s ease;
  background-color: ${({ $selected }) => ($selected ? "#f0fdf4" : "white")};
  font-weight: ${({ $selected }) => ($selected ? "500" : "400")};

  &:hover {
    background-color: ${({ $selected }) => ($selected ? "#dcfce7" : "#f3f4f6")};
  }
`;

export const CountrySelectorSearch = styled.input`
  position: sticky;
  top: 0;
  display: block;
  width: 100%;
  padding: 10px 12px;
  font-size: 0.875rem;
  font-family: "Inter", sans-serif;
  border: none;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
  outline: none;
  box-sizing: border-box;
  color: #1f2937;
  z-index: 1;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    background-color: #ffffff;
    border-bottom-color: #2c5f4e;
  }
`;

export const PhoneChipEnhanced = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 7px 10px 7px 10px;
  background-color: #ffffff;
  border: 1px solid #d1fae5;
  border-radius: 6px;
  font-family: "Inter", sans-serif;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  animation: ${fadeIn} 0.2s ease forwards;
  transition: all 0.2s ease;

  &:hover {
    border-color: #a7f3d0;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
`;

export const PhoneChipFlag = styled.span`
  font-size: 1.1rem;
  line-height: 1;
  flex-shrink: 0;
`;

export const PhoneChipCode = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #2c5f4e;
  letter-spacing: 0.02em;
`;

export const PhoneChipNumber = styled.span`
  font-size: 0.85rem;
  color: #374151;
  font-weight: 400;
  letter-spacing: 0.01em;
`;

export const PhoneCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: "Inter", sans-serif;
  background-color: ${({ $count }) => ($count > 0 ? "#d1fae5" : "#f3f4f6")};
  color: ${({ $count }) => ($count > 0 ? "#065f46" : "#6b7280")};
  border: 1px solid ${({ $count }) => ($count > 0 ? "#a7f3d0" : "#e5e7eb")};
  transition: all 0.2s ease;
`;

export const EmptyPhoneStateEnhanced = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  background-color: #f9fafb;
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
  font-family: "Inter", sans-serif;
  transition: border-color 0.2s ease;
`;

export const SectionHeaderIcon = styled.span`
  font-size: 1rem;
  line-height: 1;
  flex-shrink: 0;
  margin-right: 0.25rem;
`;

// --- MultiSelect ---
export const MultiSelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const MultiSelectControl = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  min-height: 44px;
  padding: 6px 36px 6px 10px;
  border: 1px solid ${({ $open }) => ($open ? "#2c5f4e" : "#d1d5db")};
  border-radius: 8px;
  background-color: white;
  cursor: pointer;
  box-sizing: border-box;
  transition: all 0.2s ease;
  ${({ $open }) => $open && "box-shadow: 0 0 0 3px rgba(44, 95, 78, 0.1);"}

  &:hover {
    border-color: #2c5f4e;
  }
`;

export const MultiSelectChevron = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%)
    ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0)")};
  transition: transform 0.2s ease;
  pointer-events: none;
  color: #6b7280;
  display: flex;
  align-items: center;
`;

export const MultiSelectPlaceholder = styled.span`
  font-size: 0.95rem;
  color: #9ca3af;
  font-family: "Inter", sans-serif;
  user-select: none;
`;

export const MultiSelectChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 3px 8px;
  background-color: #e6f4f1;
  border: 1px solid #a7d4cb;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #1e4236;
  font-family: "Inter", sans-serif;
  white-space: nowrap;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const MultiSelectChipRemove = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2c5f4e;
  line-height: 1;
  font-size: 0.85rem;
  flex-shrink: 0;

  &:hover {
    color: #b91c1c;
  }
`;

export const MultiSelectDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 220px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
`;

export const MultiSelectOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 9px 12px;
  cursor: pointer;
  font-size: 0.875rem;
  font-family: "Inter", sans-serif;
  color: #374151;
  transition: background-color 0.15s ease;
  background-color: ${({ $selected }) => ($selected ? "#f0fdf4" : "white")};
  font-weight: ${({ $selected }) => ($selected ? "500" : "400")};

  &:hover {
    background-color: ${({ $selected }) => ($selected ? "#dcfce7" : "#f3f4f6")};
  }
`;

export const MultiSelectCheckmark = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1.5px solid ${({ $checked }) => ($checked ? "#2c5f4e" : "#d1d5db")};
  background-color: ${({ $checked }) => ($checked ? "#2c5f4e" : "white")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &::after {
    content: "";
    display: ${({ $checked }) => ($checked ? "block" : "none")};
    width: 4px;
    height: 7px;
    border-right: 2px solid white;
    border-bottom: 2px solid white;
    transform: rotate(40deg) translate(-1px, -1px);
  }
`;
